import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Input, Select, notification, Popconfirm, Tag, Space } from 'antd';
import { UserAddOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';

const { Option } = Select;

const ProjectPermissionsModal = ({ visible, onClose, project }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePermission, setInvitePermission] = useState('read');
  const [inviting, setInviting] = useState(false);
  const { getAuthToken } = useAuth();

  useEffect(() => {
    if (visible && project) {
      loadPermissions();
    }
  }, [visible, project]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await apiService.getProjectPermissions(project.id, token);
      setPermissions(response.permissions || []);
    } catch (error) {
      console.error('Failed to load permissions:', error);
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to load project permissions'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail || !inviteEmail.trim()) {
      notification.warning({
        message: 'Warning',
        description: 'Please enter an email address'
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      notification.warning({
        message: 'Warning',
        description: 'Please enter a valid email address'
      });
      return;
    }

    try {
      setInviting(true);
      const token = getAuthToken();
      await apiService.inviteUserToProject(project.id, inviteEmail, invitePermission, token);
      
      notification.success({
        message: 'Success',
        description: `User ${inviteEmail} has been invited with ${invitePermission} permission`
      });

      setInviteEmail('');
      setInvitePermission('read');
      loadPermissions();
    } catch (error) {
      console.error('Failed to invite user:', error);
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to invite user'
      });
    } finally {
      setInviting(false);
    }
  };

  const handleUpdatePermission = async (userId, newPermission) => {
    try {
      const token = getAuthToken();
      await apiService.updateProjectPermission(project.id, userId, newPermission, token);
      
      notification.success({
        message: 'Success',
        description: 'Permission updated successfully'
      });

      loadPermissions();
    } catch (error) {
      console.error('Failed to update permission:', error);
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to update permission'
      });
    }
  };

  const handleRemovePermission = async (userId, userEmail) => {
    try {
      const token = getAuthToken();
      await apiService.removeProjectPermission(project.id, userId, token);
      
      notification.success({
        message: 'Success',
        description: `Access removed for ${userEmail}`
      });

      loadPermissions();
    } catch (error) {
      console.error('Failed to remove permission:', error);
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to remove permission'
      });
    }
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'manage':
        return 'purple';
      case 'action':
        return 'blue';
      case 'read':
        return 'green';
      default:
        return 'default';
    }
  };

  const getPermissionDescription = (permission) => {
    switch (permission) {
      case 'manage':
        return 'Can edit configuration';
      case 'action':
        return 'Can upload files and view reports';
      case 'read':
        return 'Can only view reports';
      default:
        return '';
    }
  };

  const columns = [
    {
      title: 'User Email',
      dataIndex: 'user_email',
      key: 'user_email',
      render: (email) => <strong>{email}</strong>
    },
    {
      title: 'Permission Level',
      dataIndex: 'permission_level',
      key: 'permission_level',
      render: (permission, record) => (
        <Select
          value={permission}
          style={{ width: 140 }}
          onChange={(value) => handleUpdatePermission(record.user_id, value)}
        >
          <Option value="read">
            <Tag color={getPermissionColor('read')}>Read</Tag>
          </Option>
          <Option value="action">
            <Tag color={getPermissionColor('action')}>Action</Tag>
          </Option>
          <Option value="manage">
            <Tag color={getPermissionColor('manage')}>Manage</Tag>
          </Option>
        </Select>
      )
    },
    {
      title: 'Description',
      dataIndex: 'permission_level',
      key: 'description',
      render: (permission) => (
        <span style={{ color: '#888', fontSize: '12px' }}>
          {getPermissionDescription(permission)}
        </span>
      )
    },
    {
      title: 'Granted',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Popconfirm
          title="Remove Access"
          description={`Remove access for ${record.user_email}?`}
          onConfirm={() => handleRemovePermission(record.user_id, record.user_email)}
          okText="Remove"
          cancelText="Cancel"
          okType="danger"
        >
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />}
            size="small"
          >
            Remove
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <Modal
      title={
        <div>
          <UserAddOutlined style={{ marginRight: 8 }} />
          Manage Project Access - {project?.name}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
    >
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ marginBottom: 12 }}>Invite User by Email</h4>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="Enter user email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onPressEnter={handleInviteUser}
            style={{ flex: 1 }}
          />
          <Select
            value={invitePermission}
            onChange={setInvitePermission}
            style={{ width: 140 }}
          >
            <Option value="read">
              <Tag color={getPermissionColor('read')}>Read</Tag>
            </Option>
            <Option value="action">
              <Tag color={getPermissionColor('action')}>Action</Tag>
            </Option>
            <Option value="manage">
              <Tag color={getPermissionColor('manage')}>Manage</Tag>
            </Option>
          </Select>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={handleInviteUser}
            loading={inviting}
          >
            Invite
          </Button>
        </Space.Compact>
        <div style={{ marginTop: 8, fontSize: '12px', color: '#888' }}>
          <div><strong>Read:</strong> View reports only</div>
          <div><strong>Action:</strong> Upload files and view reports</div>
          <div><strong>Manage:</strong> Edit configuration, upload files, and view reports</div>
        </div>
      </div>

      <h4 style={{ marginBottom: 12 }}>Current Access ({permissions.length})</h4>
      <Table
        dataSource={permissions}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={false}
        locale={{
          emptyText: 'No users have been granted access yet'
        }}
      />
    </Modal>
  );
};

export default ProjectPermissionsModal;
